import { Download, FileArchive, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useFormat } from '@nucleus-commerce/i18n/react';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@nucleus-commerce/ui/components/card';
import { Spinner } from '@nucleus-commerce/ui/components/spinner';

import { ServerError } from '@/shared/ui/server-error';

import { useOrganizationExport } from '../hooks/use-organization-export';

export interface ExportCardProps {
  organizationId: string;
  /** Owners and admins start exports; everyone else reads what the card is for. */
  canManage: boolean;
  className?: string;
}

/**
 * Offboarding and data requests: one zip of CSVs (products, and orders, customers and inventory
 * per store) built on the worker. The signed link needs no sign-in and lasts 24 hours.
 */
export function ExportCard({ organizationId, canManage, className }: ExportCardProps) {
  const { t } = useTranslation('organizations');
  const format = useFormat();
  const { start, tracker, job } = useOrganizationExport(organizationId);
  const working = job !== undefined && (job.status === 'queued' || job.status === 'running');

  return (
    <Card className={className} data-tour="platform.organizations.export">
      <CardHeader>
        <CardTitle>{t('export.title')}</CardTitle>
        <CardDescription>{t('export.description')}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {start.isError ? <ServerError error={start.error} /> : null}
        {tracker.status === 'error' ? <ServerError error={tracker.error} /> : null}
        {working ? (
          <p className="text-muted-foreground flex items-center gap-2 text-sm" role="status">
            <Spinner label={null} />
            {t(`export.status.${job.status}`)}
          </p>
        ) : null}
        {working && tracker.status === 'stalled' ? (
          <Button
            variant="outline"
            size="sm"
            className="self-start"
            disabled={!tracker.canCheck}
            onClick={tracker.checkNow}
          >
            <RefreshCw aria-hidden />
            {t('export.checkNow')}
          </Button>
        ) : null}
        {job?.status === 'completed' && job.downloadUrl ? (
          <div className="flex flex-col gap-2" role="status">
            <Button asChild className="self-start">
              <a href={job.downloadUrl} download>
                <Download aria-hidden />
                {t('export.download')}
              </a>
            </Button>
            {job.expiresAt ? (
              <p className="text-muted-foreground text-xs" title={format.utc(job.expiresAt)}>
                {t('export.expires', { date: format.dateTime(job.expiresAt) })}
              </p>
            ) : null}
          </div>
        ) : null}
        {job?.status === 'failed' ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{t('export.failed', { error: job.error ?? '' })}</AlertDescription>
          </Alert>
        ) : null}
        {canManage && !working ? (
          <Button
            variant="outline"
            className="self-start"
            disabled={start.isPending}
            onClick={() => start.mutate()}
          >
            <FileArchive aria-hidden />
            {job ? t('export.startAgain') : t('export.start')}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}
